import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useIdleReady } from '@/hooks/useIdleReady';
import { getNativeDeviceProfile } from '@/utils/nativeDevice';
import { prefetchHomeDashboardData } from '@/routes/prefetchData';
import {
  prefetchAuthedRoutes,
  prefetchNativeCoreRoutes,
  prefetchPublicRoutes,
  prefetchSharedRoutes,
  shouldPrefetch,
} from '@/routes/prefetchRoutes';

export function RoutePrefetcher() {
  const isProd = import.meta.env.PROD;
  const { user, loading } = useAuth();
  const queryClient = useQueryClient();
  const nativeDevice = React.useMemo(() => getNativeDeviceProfile(), []);
  const ready = useIdleReady(nativeDevice.isNativeRuntime ? 300 : 2000);
  const didPrefetch = React.useRef(false);

  React.useEffect(() => {
    if (!isProd) return;
    if (!ready || loading || didPrefetch.current) return;
    if (!shouldPrefetch()) return;

    didPrefetch.current = true;

    if (nativeDevice.isNativeRuntime) {
      void prefetchNativeCoreRoutes();
      if (user?.id) {
        void prefetchHomeDashboardData(queryClient, user.id);
      }
      return;
    }

    if (user) {
      void prefetchAuthedRoutes();
      void prefetchHomeDashboardData(queryClient, user.id);
    } else {
      void prefetchPublicRoutes();
    }

    void prefetchSharedRoutes();
  }, [ready, loading, user, isProd, nativeDevice.isNativeRuntime, queryClient]);

  return null;
}

export default RoutePrefetcher;
